import { useEffect, useState } from 'react';
import { Cpu, MemoryStick, Activity } from 'lucide-react';
import './StatsPanel.css';

interface VpsStats {
  timestamp: string;
  host: {
    cpuPercent: number;
    ramUsedMB: number;
    ramTotalMB: number;
    ramPercent: number;
    loadAvg1: number;
    uptimeSecs: number;
  };
  game: {
    name: string;
    status: string;
    cpuPercent: number;
    ramUsedMB: number;
    ramLimitMB: number;
    ramPercent: number;
  };
  ftp?: {
    name: string;
    status: string;
    cpuPercent: number;
    ramUsedMB: number;
    ramLimitMB: number;
    ramPercent: number;
  };
}

interface Props {
  serverId: string;
  isActive: boolean;
  /** Show the internal "Recursos" header with the live indicator.
   *  Set false when embedding in a modal that already provides its own title. */
  showHeader?: boolean;
}

function formatMB(mb: number): string {
  if (mb >= 1024) return `${(mb / 1024).toFixed(2)} GB`;
  return `${mb.toFixed(0)} MB`;
}

function colorForPercent(pct: number): string {
  if (pct >= 90) return '#ef4444';
  if (pct >= 70) return '#f59e0b';
  return '#22c55e';
}

function formatUptime(secs: number): string {
  const d = Math.floor(secs / 86400);
  const h = Math.floor((secs % 86400) / 3600);
  const m = Math.floor((secs % 3600) / 60);
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

export function StatsPanel({ serverId, isActive, showHeader = true }: Props) {
  const [stats, setStats] = useState<VpsStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!isActive) {
      setStats(null);
      setError(null);
      setConnected(false);
      return;
    }

    // One persistent SSE connection per session, instead of polling every 3s.
    // EventSource auto-reconnects on transient network drops. We close it
    // explicitly on unmount or when the server becomes inactive.
    const url = `${import.meta.env.VITE_BACKEND_URL}/api/servers/${serverId}/stats/stream`;
    const source = new EventSource(url, { withCredentials: true });

    source.onopen = () => {
      setConnected(true);
      setError(null);
    };

    source.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data) as VpsStats;
        setStats(parsed);
      } catch {
        // Ignore malformed frames; the next tick will replace them.
      }
    };

    source.onerror = () => {
      // EventSource auto-reconnects. We only mark as disconnected if the
      // server explicitly closed (readyState === CLOSED).
      if (source.readyState === EventSource.CLOSED) {
        setConnected(false);
        setError('Stream cerrado por el servidor');
      }
    };

    return () => {
      source.close();
      setConnected(false);
    };
  }, [serverId, isActive]);

  if (!isActive) {
    return (
      <div className="stats-panel stats-panel--inactive">
        <div className="stats-panel__header">
          <Activity size={18} />
          <h3>Recursos</h3>
        </div>
        <p className="stats-panel__empty">Inicia el servidor para ver el consumo en vivo.</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="stats-panel">
        <div className="stats-panel__header">
          <Activity size={18} />
          <h3>Recursos</h3>
        </div>
        <p className="stats-panel__loading">
          {error || 'Conectando al stream...'}
        </p>
      </div>
    );
  }

  const hostCpuColor = colorForPercent(stats.host.cpuPercent);
  const hostRamColor = colorForPercent(stats.host.ramPercent);

  return (
    <div className="stats-panel">
      {showHeader && (
        <div className="stats-panel__header">
          <Activity size={18} />
          <h3>Recursos</h3>
          <span className="stats-panel__live" title="Actualización en vivo">● en vivo</span>
        </div>
      )}

      <section className="stats-panel__section">
        <h4>VPS (host)</h4>
        <div className="stats-panel__row">
          <div className="stats-panel__metric">
            <div className="stats-panel__metric-header">
              <Cpu size={14} />
              <span>CPU</span>
              <span className="stats-panel__value" style={{ color: hostCpuColor }}>
                {stats.host.cpuPercent.toFixed(1)}%
              </span>
            </div>
            <div className="stats-panel__bar">
              <div
                className="stats-panel__bar-fill"
                style={{ width: `${Math.min(100, stats.host.cpuPercent)}%`, background: hostCpuColor }}
              />
            </div>
            <span className="stats-panel__hint">load avg 1m: {stats.host.loadAvg1.toFixed(2)}</span>
          </div>

          <div className="stats-panel__metric">
            <div className="stats-panel__metric-header">
              <MemoryStick size={14} />
              <span>RAM</span>
              <span className="stats-panel__value" style={{ color: hostRamColor }}>
                {stats.host.ramPercent.toFixed(1)}%
              </span>
            </div>
            <div className="stats-panel__bar">
              <div
                className="stats-panel__bar-fill"
                style={{ width: `${Math.min(100, stats.host.ramPercent)}%`, background: hostRamColor }}
              />
            </div>
            <span className="stats-panel__hint">
              {formatMB(stats.host.ramUsedMB)} / {formatMB(stats.host.ramTotalMB)}
            </span>
          </div>
        </div>
        <span className="stats-panel__uptime">Uptime: {formatUptime(stats.host.uptimeSecs)}</span>
      </section>
    </div>
  );
}